/**
 * IncidentCommanderTab · v2.6.9 batch Q
 *
 * Admin / dispatch top-level "TAXIcab Commander" tab. PIN-gated (per-account
 * PIN, same flow as the Accounting tab). After unlock, exposes two
 * sub-sections:
 *   1. Incidents — list/detail with status workflow + conflict prevention
 *   2. Laws & Regulations — searchable statute database with print
 *
 * All dispatch roles (admin / supervisor / dispatcher / team_leader) can
 * unlock with their own PIN. Conflict prevention is enforced server-side
 * (route returns 403 when the actor was the original submitter unless they
 * are admin AND opt into admin_override).
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Loader2, Lock, Search, Shield, FileText, Printer, Plus, Trash2, Edit2, ScrollText, Eye, X, Flag } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import useSiteConfig from "@/hooks/useSiteConfig";
import BrandLogo from "@/components/BrandLogo";

const STATUSES = ["Submitted", "Under Review", "Under Investigation", "Law Enforcement Review", "Flagged", "Cleared by Safety Team"];

export default function IncidentCommanderTab() {
  const [tab, setTab] = useState("incidents");
  const { cfg } = useSiteConfig();
  const commanderLogo = cfg?.global?.taxi_commander_logo_url;
  return (
    <div className="space-y-4" data-testid="commander-unlocked">
      {/* v2.6.9 batch CF · TAXICOMMANDER brand mark at the top of this tab.
          Renders only when the CMS has a URL (default seeded).
          v2.6.9 batch CH · Scaled down 50% (size md → sm) per spec. Centered.
          v2.6.9 batch CI · Bumped back up to size="lg" per spec — admin
          console wants the mark larger and more prominent. */}
      {commanderLogo && (
        <div className="flex items-center justify-center pb-1" data-testid="commander-brand-mark">
          <BrandLogo src={commanderLogo} alt="TAXIcab Commander" size="lg"/>
        </div>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800 grid grid-cols-2 mb-4 h-auto p-1 gap-1">
          <TabsTrigger value="incidents" data-testid="commander-tab-incidents" className="text-xs sm:text-sm py-2"><AlertTriangle className="h-3.5 w-3.5 mr-1"/>Incidents</TabsTrigger>
          <TabsTrigger value="statutes" data-testid="commander-tab-statutes" className="text-xs sm:text-sm py-2"><ScrollText className="h-3.5 w-3.5 mr-1"/>Laws & Regulations</TabsTrigger>
        </TabsList>
        <TabsContent value="incidents"><IncidentsGate/></TabsContent>
        <TabsContent value="statutes"><StatutesPanel/></TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Incidents-only PIN gate. Laws & Regulations (the other sub-tab) is open
 * to any dispatch role and renders without a PIN per user request — incident
 * reports remain PIN-gated because they contain sensitive personal info.
 */
function IncidentsGate() {
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [busy, setBusy] = useState(false);

  const unlock = async (e) => {
    e?.preventDefault();
    if (!pin) return;
    setBusy(true);
    try {
      await adminApi.post("/admin/incident-commander/unlock", { pin });
      setUnlocked(true);
      toast.success("Incidents unlocked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlock failed");
    } finally { setBusy(false); }
  };

  if (!unlocked) {
    return (
      <Card className="bg-zinc-900 border-amber-400/30 p-6 max-w-md mx-auto" data-testid="commander-gate">
        <div className="flex items-center gap-3 mb-3">
          <Lock className="h-5 w-5 text-amber-400"/>
          <div>
            <h3 className="font-bold">Incident Reports</h3>
            <p className="text-[11px] text-zinc-500">Enter your supervisor / admin / dispatch PIN to access driver and rider incident reports. Laws & Regulations is open without a PIN.</p>
          </div>
        </div>
        <form onSubmit={unlock} className="flex gap-2">
          <Input data-testid="commander-pin-input" type="password" inputMode="numeric" autoFocus value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" className="bg-zinc-950 border-zinc-700"/>
          <Button data-testid="commander-unlock-btn" type="submit" disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Unlock"}</Button>
        </form>
      </Card>
    );
  }

  return <IncidentsPanel pin={pin}/>;
}

// =================== Incidents ===================
function IncidentsPanel({ pin }) {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const reload = useCallback(async () => {
    try {
      const params = { pin, status: statusFilter, q: q.trim(), limit: 200 };
      const { data } = await adminApi.get("/admin/incidents", { params });
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load incidents");
    }
  }, [pin, statusFilter, q]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-3">
      <Card className="bg-zinc-900 border-zinc-800 p-3 flex items-center gap-2 flex-wrap" data-testid="incidents-toolbar">
        <Search className="h-4 w-4 text-zinc-500"/>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search incident #, driver ID, customer name, rider ID, narrative, case#…" className="bg-zinc-950 border-zinc-700 flex-1 min-w-[180px]" data-testid="incidents-search"/>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-zinc-950 border-zinc-700 w-[200px]" data-testid="incidents-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[10px] text-zinc-500 ml-auto">{count} report{count === 1 ? "" : "s"}</span>
      </Card>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-xs text-zinc-500 text-center py-8">No reports match your filters.</div>}
        {items.map((i) => (
          <Card key={i.id} className="bg-zinc-900 border-zinc-800 p-3 flex items-start gap-3" data-testid={`incident-row-${i.id}`}>
            <AlertTriangle className={`h-5 w-5 mt-1 flex-shrink-0 ${i.status === "Flagged" ? "text-red-400" : "text-amber-400"}`}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-bold">{i.incident_number}</div>
                <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${i.status === "Flagged" ? "bg-red-500/20 text-red-300" : "bg-amber-400/15 text-amber-300"}`}>{i.status}</span>
              </div>
              <div className="text-[11px] text-zinc-400">
                {i.incident_date} {i.incident_time} · <span className={`font-semibold ${i.submitter_type === "driver" ? "text-amber-300" : "text-blue-300"}`}>{i.submitter_type === "driver" ? "Driver" : "Rider"}</span> · {i.submitter_name}
                {i.driver_kind ? ` · ${i.driver_kind}` : ""}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setOpenId(i.id)} className="text-amber-400" data-testid={`open-incident-${i.id}`}>
              <Eye className="h-4 w-4 mr-1"/>Review
            </Button>
          </Card>
        ))}
      </div>

      <IncidentDetailDialog incidentId={openId} pin={pin} onClose={() => setOpenId(null)} onChange={reload}/>
    </div>
  );
}

function IncidentDetailDialog({ incidentId, pin, onClose, onChange }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusDraft, setStatusDraft] = useState("");
  const [note, setNote] = useState("");
  const [override, setOverride] = useState(false);
  // v2.6.9 batch X · flag-rider workflow inside the incident dialog
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagForm, setFlagForm] = useState({ customer_email: "", customer_id: "", reason: "", severity: "warning" });
  const [flagBusy, setFlagBusy] = useState(false);

  useEffect(() => {
    if (!incidentId) { setD(null); return; }
    setD(null); setStatusDraft(""); setNote(""); setOverride(false); setFlagOpen(false);
    adminApi.get(`/admin/incidents/${incidentId}`, { params: { pin } })
      .then(r => { setD(r.data); setStatusDraft(r.data.status); })
      .catch(e => toast.error(e?.response?.data?.detail || "Load failed"));
  }, [incidentId, pin]);

  const submitFlag = async () => {
    if (!flagForm.reason.trim()) { toast.error("Reason required"); return; }
    if (!flagForm.customer_id.trim() && !flagForm.customer_email.trim()) { toast.error("Provide rider email or rider ID"); return; }
    setFlagBusy(true);
    try {
      const body = {
        pin,
        reason: flagForm.reason.trim(),
        severity: flagForm.severity,
        customer_id: flagForm.customer_id.trim() || null,
        customer_email: flagForm.customer_email.trim() || null,
      };
      const r = await adminApi.post(`/admin/incidents/${incidentId}/flag-rider`, body);
      toast.success(`Rider flagged (${r.data.flag.severity})`);
      setFlagForm({ customer_email: "", customer_id: "", reason: "", severity: "warning" });
      setFlagOpen(false);
      const { data } = await adminApi.get(`/admin/incidents/${incidentId}`, { params: { pin } });
      setD(data);
      if (typeof onChange === "function") onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Flag failed"); }
    finally { setFlagBusy(false); }
  };

  const updateStatus = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/incidents/${incidentId}/status`, { pin, status: statusDraft, note: note.trim() || null, admin_override: override });
      toast.success(`Status → ${statusDraft}`);
      const { data } = await adminApi.get(`/admin/incidents/${incidentId}`, { params: { pin } });
      setD(data); setNote(""); setOverride(false);
      if (typeof onChange === "function") onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const printOpen = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("rtg_admin_token");
    fetch(`${base}/api/admin/incidents/${incidentId}/print.html?pin=${encodeURIComponent(pin)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.text() : Promise.reject(r.statusText))
      .then(html => { const blob = new Blob([html], { type: "text/html" }); window.open(URL.createObjectURL(blob), "_blank"); })
      .catch(err => toast.error(`Print failed: ${err}`));
  };

  return (
    <Dialog open={!!incidentId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="incident-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400"/>{d?.incident_number || "Incident"}</span>
            <Button size="sm" onClick={printOpen} disabled={!d} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="incident-print-btn"><Printer className="h-4 w-4 mr-1"/>Print / PDF</Button>
          </DialogTitle>
        </DialogHeader>
        {!d ? <div className="py-10 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2"/>Loading…</div> : (
          <div className="space-y-4 text-sm">
            <Card className="bg-zinc-950 border-zinc-800 p-3 grid grid-cols-2 gap-2">
              <KV k="Submitter" v={`${d.submitter_name} (${d.submitter_type})`}/>
              <KV k="When" v={`${d.incident_date} ${d.incident_time}`}/>
              <KV k="Driver kind" v={d.driver_kind || "—"}/>
              <KV k="Dashcam" v={d.has_dashcam ? "Yes" : "No"}/>
              <KV k="Submitter phone" v={d.submitter_phone || "—"}/>
              <KV k="Submitter address" v={d.submitter_address || "—"}/>
              <KV k="Other-party phone" v={d.other_party_phone || "—"}/>
              <KV k="Other-party address" v={d.other_party_address || "—"}/>
              <KV k="Ride start" v={d.starting_address || "—"}/>
              <KV k="Ride end" v={d.ending_address || "—"}/>
              {(d.police_officer_name || d.police_jurisdiction || d.police_case_number) && (
                <>
                  <KV k="Officer" v={d.police_officer_name || "—"}/>
                  <KV k="Jurisdiction" v={d.police_jurisdiction || "—"}/>
                  <KV k="Case #" v={d.police_case_number || "—"}/>
                </>
              )}
            </Card>

            <Card className="bg-zinc-950 border-zinc-800 p-3" data-testid="incident-narrative">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1">Narrative</div>
              <div className="text-xs whitespace-pre-wrap leading-relaxed">{d.body}</div>
            </Card>

            <Card className="bg-zinc-950 border-zinc-800 p-3" data-testid="incident-media-grid">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">Media · {d.media?.length || 0} item(s)</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {(d.media || []).map((m) => (
                  <a key={m.file_id} href={m.url} target="_blank" rel="noreferrer" className="block border border-zinc-800 rounded overflow-hidden">
                    {m.kind === "photo" ? (
                      <img src={m.url} alt={m.original_name || ""} className="w-full h-20 object-cover"/>
                    ) : (
                      <div className="h-20 flex items-center justify-center text-xs text-zinc-400">▶ video</div>
                    )}
                  </a>
                ))}
              </div>
              {d.external_video_url && (
                <a href={d.external_video_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-amber-400 hover:underline" data-testid="incident-external-link">
                  <FileText className="h-3.5 w-3.5"/>External video link →
                </a>
              )}
              {(!d.media || d.media.length === 0) && !d.external_video_url && <div className="text-[11px] text-zinc-600 italic">None attached.</div>}
            </Card>

            <Card className="bg-zinc-950 border-amber-400/30 p-3" data-testid="incident-status-controls">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">Status workflow</div>
              {d._is_submitter && !d._admin_override_available && (
                <div className="rounded bg-red-500/10 border border-red-500/30 p-2 mb-2 text-[11px] text-red-200" data-testid="incident-conflict-block">
                  <Shield className="h-3.5 w-3.5 inline mr-1"/>Company / safety policy: you submitted this report and cannot change its status. Another supervisor or dispatcher must review it.
                </div>
              )}
              {d._is_submitter && d._admin_override_available && (
                <label className="flex items-center gap-2 mb-2 text-[11px] text-amber-200" data-testid="incident-admin-override-toggle">
                  <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)}/>
                  Admin override · I am the submitter; route this change through the audit chain anyway.
                </label>
              )}
              <div className="flex flex-col sm:flex-row gap-2 mb-2">
                <Select value={statusDraft} onValueChange={setStatusDraft} disabled={d._is_submitter && !d._admin_override_available}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-700 sm:w-[260px]" data-testid="incident-status-select"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional internal note" className="bg-zinc-950 border-zinc-700 flex-1" data-testid="incident-status-note"/>
                <Button onClick={updateStatus} disabled={busy || (d._is_submitter && !override) || statusDraft === d.status} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="incident-status-save">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Save"}
                </Button>
              </div>
              <div className="text-[10px] text-zinc-500">All status changes are recorded in the audit trail below.</div>
            </Card>

            {/* v2.6.9 batch X · Flag Rider workflow */}
            <Card className="bg-zinc-950 border-red-500/30 p-3" data-testid="incident-flag-rider">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider text-red-300 font-bold">Flag Rider · Post-Review Action</div>
                {!flagOpen && (
                  <Button size="sm" onClick={() => setFlagOpen(true)} className="bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-200 text-[11px] h-7" data-testid="incident-flag-open-btn"><Flag className="h-3 w-3 mr-1"/>Flag Rider</Button>
                )}
              </div>
              <div className="text-[10px] text-zinc-500 mb-2">Use after the incident is reviewed. Flagged riders trigger a 🚩 alert in dispatcher + driver apps the next time they book.</div>
              {flagOpen && (
                <div className="space-y-2 bg-red-500/5 border border-red-500/20 rounded p-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-zinc-400">Rider Email</Label>
                      <Input data-testid="flag-rider-email" value={flagForm.customer_email} onChange={(e) => setFlagForm((s) => ({ ...s, customer_email: e.target.value }))} placeholder="rider@example.com" className="bg-zinc-950 border-zinc-700 h-8 mt-1"/>
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-400">— OR — Rider ID</Label>
                      <Input data-testid="flag-rider-id" value={flagForm.customer_id} onChange={(e) => setFlagForm((s) => ({ ...s, customer_id: e.target.value }))} placeholder="cust-abc123…" className="bg-zinc-950 border-zinc-700 h-8 mt-1 font-mono text-[11px]"/>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-zinc-400">Severity</Label>
                    <Select value={flagForm.severity} onValueChange={(v) => setFlagForm((s) => ({ ...s, severity: v }))}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-700 h-8 mt-1" data-testid="flag-rider-severity"><SelectValue/></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                        <SelectItem value="watch">Watch · informational</SelectItem>
                        <SelectItem value="warning">Warning · alert on booking</SelectItem>
                        <SelectItem value="block">Block · prevent future bookings</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-zinc-400">Reason (required)</Label>
                    <Input data-testid="flag-rider-reason" value={flagForm.reason} onChange={(e) => setFlagForm((s) => ({ ...s, reason: e.target.value }))} placeholder="e.g., Verbal abuse · refusal to pay · disruptive behavior" className="bg-zinc-950 border-zinc-700 h-8 mt-1"/>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={submitFlag} disabled={flagBusy} className="bg-red-500 hover:bg-red-400 text-white text-[11px] h-8 flex-1" data-testid="flag-rider-submit">{flagBusy ? <Loader2 className="h-3 w-3 animate-spin"/> : "🚩 SUBMIT FLAG"}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setFlagOpen(false)} className="text-[11px] h-8">Cancel</Button>
                  </div>
                </div>
              )}
            </Card>

            <Card className="bg-zinc-950 border-zinc-800 p-3" data-testid="incident-audit">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">Audit Trail · {(d.audit_trail || []).length} event(s)</div>
              <div className="space-y-1 max-h-56 overflow-y-auto">
                {(d.audit_trail || []).map((a, idx) => (
                  // v2.6.9 batch S-2 · stable composite key (ts + action +
                  // to_status) since audit entries don't carry their own id.
                  // Falls back to idx for the impossible case where every
                  // field is empty.
                  <div key={`${a.ts || ""}-${a.action || ""}-${a.to_status || ""}-${idx}`} className="text-[10px] border border-zinc-800 rounded p-2">
                    <div className="font-semibold text-zinc-300">{new Date(a.ts).toLocaleString()} · {a.actor} <span className="text-zinc-500">({a.actor_role})</span></div>
                    <div className="text-zinc-400">{a.action} · {a.from_status || "—"} → <span className="text-amber-300">{a.to_status}</span> {a.admin_override ? <span className="text-red-300 ml-1">[admin override]</span> : null}</div>
                    {a.note && <div className="text-zinc-500 italic mt-0.5">"{a.note}"</div>}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KV({ k, v }) {
  return <div className="text-xs"><span className="text-zinc-500">{k}: </span><span className="text-zinc-200 font-medium">{v}</span></div>;
}

// =================== Statutes ===================
// Static fallback in case the dynamic jurisdictions endpoint is slow.
// The dropdown is rebuilt from the DB on mount so any newly-seeded
// jurisdiction shows up automatically.
const FALLBACK_JURISDICTIONS = [
  "Oklahoma State", "Oklahoma County", "City of Weatherford",
  "FMCSA (Federal)", "OSHA (Federal)", "Federal Civil Rights",
];
const JUR_BADGE = {
  "Oklahoma State": "bg-blue-500/20 text-blue-300 border-blue-500/40",
  "Oklahoma County": "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
  "FMCSA (Federal)": "bg-purple-500/20 text-purple-300 border-purple-500/40",
  "OSHA (Federal)": "bg-orange-500/20 text-orange-300 border-orange-500/40",
  "Federal Civil Rights": "bg-pink-500/20 text-pink-300 border-pink-500/40",
};
const CITY_BADGE = "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
const FALLBACK_BADGE = "bg-zinc-800 text-zinc-300 border-zinc-600";
function jurClass(j) {
  if (!j) return FALLBACK_BADGE;
  return JUR_BADGE[j] || (j.startsWith("City of ") ? CITY_BADGE : FALLBACK_BADGE);
}

// Build a paginator window: [1, …, current-1, current, current+1, …, last].
// Always shows first + last; collapses to "…" outside a 2-page window of current.
function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current, current - 1, current + 1]);
  const arr = Array.from(set).filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    out.push(arr[i]);
    if (i < arr.length - 1 && arr[i + 1] - arr[i] > 1) out.push("…");
  }
  return out;
}

function StatutesPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [q, setQ] = useState("");
  const [jurisdiction, setJurisdiction] = useState("all");
  const [viewing, setViewing] = useState(null); // full statute object
  const [editing, setEditing] = useState(null); // {id?, title, content, jurisdiction, citation, source_url}
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/statutes", {
        params: { q: q.trim(), jurisdiction, page, page_size: 25 },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load statutes"); }
  }, [q, jurisdiction, page]);
  useEffect(() => { reload(); }, [reload]);

  // Reset to page 1 whenever the filters change
  useEffect(() => { setPage(1); }, [q, jurisdiction]);

  // Dynamically load the jurisdiction list from the backend so newly-seeded
  // cities/counties show up in the dropdown automatically (state has 77
  // counties + ~290 cities, far too many for a hardcoded list).
  const [jurOptions, setJurOptions] = useState(FALLBACK_JURISDICTIONS.map((j) => ({ jurisdiction: j, count: 0 })));
  useEffect(() => {
    adminApi.get("/admin/statutes-jurisdictions")
      .then(({ data }) => { if (data?.items?.length) setJurOptions(data.items); })
      .catch(() => { /* keep fallback */ });
  }, []);
  const [jurSearch, setJurSearch] = useState("");
  const filteredJurOptions = useMemo(() => {
    const needle = jurSearch.trim().toLowerCase();
    if (!needle) return jurOptions;
    return jurOptions.filter((o) => o.jurisdiction.toLowerCase().includes(needle));
  }, [jurOptions, jurSearch]);

  const openView = async (id) => {
    try {
      const { data } = await adminApi.get(`/admin/statutes/${id}`);
      setViewing(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load statute"); }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.title?.trim() || !editing.content?.trim()) { toast.error("Title and content are required"); return; }
    setBusy(true);
    try {
      const body = {
        title: editing.title,
        content: editing.content,
        jurisdiction: editing.jurisdiction || null,
        citation: editing.citation || null,
        source_url: editing.source_url || null,
      };
      if (editing.id) {
        await adminApi.put(`/admin/statutes/${editing.id}`, body);
        toast.success("Statute updated");
      } else {
        await adminApi.post("/admin/statutes", body);
        toast.success("Statute added");
      }
      setEditing(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this statute?")) return;
    try {
      await adminApi.delete(`/admin/statutes/${id}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const printStatute = (id) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("rtg_admin_token");
    fetch(`${base}/api/admin/statutes/${id}/print.html`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.text() : Promise.reject(r.statusText))
      .then(html => { const blob = new Blob([html], { type: "text/html" }); window.open(URL.createObjectURL(blob), "_blank"); })
      .catch(err => toast.error(`Print failed: ${err}`));
  };

  const printSet = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("rtg_admin_token");
    const params = new URLSearchParams();
    if (jurisdiction && jurisdiction !== "all") params.set("jurisdiction", jurisdiction);
    if (q.trim()) params.set("q", q.trim());
    const qs = params.toString();
    const label = jurisdiction === "all" ? "All Jurisdictions" : jurisdiction;
    toast.info(`Building print set for ${label}…`);
    fetch(`${base}/api/admin/statutes-print-set.html${qs ? `?${qs}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.text() : Promise.reject(r.statusText))
      .then(html => { const blob = new Blob([html], { type: "text/html" }); window.open(URL.createObjectURL(blob), "_blank"); })
      .catch(err => toast.error(`Print set failed: ${err}`));
  };

  const seedLibrary = async () => {
    if (!window.confirm("Add the 25 pre-curated Oklahoma + Weatherford + FMCSA + OSHA laws? Existing titles will be skipped.")) return;
    setSeeding(true);
    try {
      const { data } = await adminApi.post("/admin/statutes/seed-starter-library", {});
      toast.success(`Library seeded · ${data.inserted} added · ${data.skipped} already existed`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
    finally { setSeeding(false); }
  };

  const seedComprehensive = async () => {
    if (!window.confirm("Add the comprehensive law library: U.S. Constitution + Bill of Rights + 27 Amendments + Oklahoma Constitution + ~30 Oklahoma statutes + 8 Weatherford Code chapters (~77 entries). Existing titles will be skipped.")) return;
    setSeeding(true);
    try {
      const { data } = await adminApi.post("/admin/statutes/seed-comprehensive", {});
      toast.success(`Comprehensive library seeded · ${data.inserted} added · ${data.skipped} already existed`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
    finally { setSeeding(false); }
  };

  const wordCount = useMemo(() => (editing?.content || "").trim().split(/\s+/).filter(Boolean).length, [editing]);

  return (
    <div className="space-y-3">
      <Card className="bg-zinc-900 border-zinc-800 p-3 flex items-center gap-2 flex-wrap" data-testid="statutes-toolbar">
        <Search className="h-4 w-4 text-zinc-500"/>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search statute name or keywords…" className="bg-zinc-950 border-zinc-700 flex-1 min-w-[180px]" data-testid="statutes-search"/>
        <Select value={jurisdiction} onValueChange={setJurisdiction}>
          <SelectTrigger className="bg-zinc-950 border-zinc-700 w-[220px]" data-testid="statutes-jurisdiction-filter"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-[420px]">
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-2 z-10">
              <Input
                value={jurSearch}
                onChange={(e) => setJurSearch(e.target.value)}
                placeholder={`Search ${jurOptions.length} jurisdictions…`}
                className="bg-zinc-950 border-zinc-700 h-8 text-xs"
                data-testid="statutes-jurisdiction-search"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
            <SelectItem value="all">All jurisdictions ({jurOptions.reduce((s, o) => s + (o.count || 0), 0)})</SelectItem>
            {filteredJurOptions.length === 0 && (
              <div className="text-xs text-zinc-500 py-3 px-3">No matches.</div>
            )}
            {filteredJurOptions.map((o) => (
              <SelectItem key={o.jurisdiction} value={o.jurisdiction}>
                {o.jurisdiction}{o.count ? ` (${o.count})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={printSet} variant="outline" className="border-zinc-600 text-zinc-200 hover:bg-zinc-800" data-testid="statute-print-set-btn" title="Print all statutes matching the current filter — TOC + grouped by jurisdiction"><Printer className="h-4 w-4 mr-1"/>Print Set</Button>
        <Button onClick={() => setEditing({ title: "", content: "", jurisdiction: "", citation: "", source_url: "" })} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="statute-new-btn"><Plus className="h-4 w-4 mr-1"/>Add Statute</Button>
        <Button onClick={seedLibrary} disabled={seeding} variant="outline" className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10" data-testid="statute-seed-btn">{seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <ScrollText className="h-4 w-4 mr-1"/>}Seed Starter Library</Button>
        <Button onClick={seedComprehensive} disabled={seeding} variant="outline" className="border-blue-400/40 text-blue-300 hover:bg-blue-400/10" data-testid="statute-seed-comprehensive-btn">{seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <ScrollText className="h-4 w-4 mr-1"/>}Seed Comprehensive Library</Button>
        <span className="text-[10px] text-zinc-500 ml-auto">{total} statute{total === 1 ? "" : "s"}{totalPages > 1 ? ` · page ${page}/${totalPages}` : ""}</span>
      </Card>

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-xs text-zinc-500 text-center py-8" data-testid="statutes-empty">
            No statutes match your search. Click <span className="text-amber-300 font-semibold">Seed Starter Library</span> to populate with 25 Oklahoma + Weatherford + FMCSA + OSHA laws.
          </div>
        )}
        {items.map((s) => (
          <Card
            key={s.id}
            onClick={() => openView(s.id)}
            className="bg-zinc-900 border-zinc-800 p-3 flex items-start gap-2 cursor-pointer hover:border-amber-400/50 hover:bg-zinc-900/80 transition"
            data-testid={`statute-row-${s.id}`}
          >
            <ScrollText className="h-4 w-4 text-amber-400 mt-1 flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-bold text-amber-50 hover:text-amber-300 transition" data-testid={`statute-title-${s.id}`}>{s.title}</div>
                {s.jurisdiction && (
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${jurClass(s.jurisdiction)}`} data-testid={`statute-jur-${s.id}`}>{s.jurisdiction}</span>
                )}
                {s.citation && <span className="text-[10px] text-zinc-400 font-mono">{s.citation}</span>}
              </div>
              <div className="text-[11px] text-zinc-500 line-clamp-2 mt-1">{s.content?.slice(0, 220)}{s.content?.length > 220 ? "…" : ""}</div>
              <div className="text-[10px] text-zinc-600 mt-1 flex items-center gap-2">
                <span>Updated {new Date(s.updated_at).toLocaleString()} by {s.updated_by}</span>
                <span className="text-amber-400/80">· Click to view full text</span>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="ghost" onClick={() => openView(s.id)} className="text-amber-400" data-testid={`statute-view-${s.id}`} title="View"><Eye className="h-3.5 w-3.5"/></Button>
              <Button size="sm" variant="ghost" onClick={() => printStatute(s.id)} className="text-amber-400" data-testid={`statute-print-${s.id}`} title="Print / PDF"><Printer className="h-3.5 w-3.5"/></Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing({ id: s.id, title: s.title, content: s.content, jurisdiction: s.jurisdiction || "", citation: s.citation || "", source_url: s.source_url || "" })} className="text-zinc-300" data-testid={`statute-edit-${s.id}`} title="Edit"><Edit2 className="h-3.5 w-3.5"/></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(s.id)} className="text-red-400" data-testid={`statute-del-${s.id}`} title="Delete"><Trash2 className="h-3.5 w-3.5"/></Button>
            </div>
          </Card>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 pt-3 flex-wrap" data-testid="statutes-pagination">
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="text-xs h-8 px-2" data-testid="statutes-prev">‹ Prev</Button>
          {pageNumbers(page, totalPages).map((n, idx) => (
            n === "…" ? (
              <span key={`ellipsis-${idx}`} className="text-xs text-zinc-600 px-1">…</span>
            ) : (
              <Button
                key={`pg-${n}`}
                size="sm"
                variant={n === page ? "default" : "ghost"}
                onClick={() => setPage(n)}
                className={`text-xs h-8 min-w-[32px] ${n === page ? "bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" : "text-zinc-300 hover:bg-zinc-800"}`}
                data-testid={`statutes-page-${n}`}
              >{n}</Button>
            )
          ))}
          <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="text-xs h-8 px-2" data-testid="statutes-next">Next ›</Button>
        </div>
      )}

      {/* View dialog · click any statute card to open */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="statute-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-start gap-3 pr-8">
              <ScrollText className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0"/>
              <div className="flex-1">
                <div className="text-base font-bold leading-tight" data-testid="statute-view-title">{viewing?.title}</div>
                <div className="flex items-center gap-2 flex-wrap mt-1.5">
                  {viewing?.jurisdiction && (
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${jurClass(viewing.jurisdiction)}`}>{viewing.jurisdiction}</span>
                  )}
                  {viewing?.citation && <span className="text-[11px] text-zinc-400 font-mono">{viewing.citation}</span>}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded p-4 text-sm leading-relaxed whitespace-pre-wrap" data-testid="statute-view-content">{viewing?.content}</div>
            {viewing?.source_url && (
              <a href={viewing.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs text-amber-300 hover:text-amber-200 underline" data-testid="statute-view-source">
                <FileText className="h-3.5 w-3.5"/> Open at official source
              </a>
            )}
            <div className="text-[10px] text-zinc-500 border-t border-zinc-800 pt-2">
              Last updated {viewing?.updated_at ? new Date(viewing.updated_at).toLocaleString() : "—"} by {viewing?.updated_by || "—"}
            </div>
            <div className="flex items-center gap-2 border-t border-zinc-800 pt-3">
              <Button onClick={() => viewing && printStatute(viewing.id)} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="statute-view-print"><Printer className="h-4 w-4 mr-1"/>Print / PDF</Button>
              <Button variant="ghost" onClick={() => { setEditing({ id: viewing.id, title: viewing.title, content: viewing.content, jurisdiction: viewing.jurisdiction || "", citation: viewing.citation || "", source_url: viewing.source_url || "" }); setViewing(null); }} data-testid="statute-view-edit"><Edit2 className="h-4 w-4 mr-1"/>Edit</Button>
              <Button variant="ghost" onClick={() => setViewing(null)} className="ml-auto"><X className="h-4 w-4 mr-1"/>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-2xl" data-testid="statute-editor">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScrollText className="h-5 w-5 text-amber-400"/>{editing?.id ? "Edit Statute" : "Add Statute"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title / Statute Name</Label>
              <Input data-testid="statute-title-input" value={editing?.title || ""} onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))} className="bg-zinc-950 border-zinc-700 mt-1" placeholder="e.g., 47 OS § 11-902 · Driving Under the Influence (DUI)"/>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Jurisdiction</Label>
                <Select value={editing?.jurisdiction || ""} onValueChange={(v) => setEditing((s) => ({ ...s, jurisdiction: v }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-700 mt-1" data-testid="statute-jurisdiction-input"><SelectValue placeholder="Select jurisdiction…"/></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-[300px]">
                    {jurOptions.map((o) => (<SelectItem key={o.jurisdiction} value={o.jurisdiction}>{o.jurisdiction}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Citation (optional)</Label>
                <Input data-testid="statute-citation-input" value={editing?.citation || ""} onChange={(e) => setEditing((s) => ({ ...s, citation: e.target.value }))} className="bg-zinc-950 border-zinc-700 mt-1" placeholder="47 O.S. § 11-902"/>
              </div>
            </div>
            <div>
              <Label className="text-xs">Source URL (optional · opens in View dialog)</Label>
              <Input data-testid="statute-source-input" value={editing?.source_url || ""} onChange={(e) => setEditing((s) => ({ ...s, source_url: e.target.value }))} className="bg-zinc-950 border-zinc-700 mt-1" placeholder="https://www.oscn.net/..."/>
            </div>
            <div>
              <Label className="text-xs">Statute Text (max 5,000 words)</Label>
              <textarea
                data-testid="statute-content-input"
                value={editing?.content || ""}
                onChange={(e) => setEditing((s) => ({ ...s, content: e.target.value }))}
                rows={12}
                className="w-full bg-zinc-950 border border-zinc-700 rounded mt-1 p-3 text-xs leading-relaxed resize-y min-h-[220px]"
                placeholder="Paste the full statute text here…"
              />
              <div className={`text-[10px] mt-1 text-right ${wordCount > 5000 ? "text-red-400 font-bold" : "text-zinc-500"}`}>{wordCount.toLocaleString()} / 5,000 words</div>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-800">
              <Button onClick={save} disabled={busy || wordCount > 5000} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="statute-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Save"}</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}><X className="h-4 w-4 mr-1"/>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
